import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';

import {isNotNil} from 'ramda';

import type {
  CreateBatchApiArg,
  CreateBatchApiResponse,
  GetBatchApiArg,
  GetBatchApiResponse,
  ListBatchVehiclesApiArg,
  ListBatchVehiclesApiResponse,
  ListBatchesApiArg,
  ListBatchesApiResponse,
  RequestPriceReportApiArg,
  RequestPriceReportApiResponse,
  ResolveVehicleWarningsApiArg,
  ResolveVehicleWarningsApiResponse,
  SetVehicleUnresolvedWarningsApiArg,
  SetVehicleUnresolvedWarningsApiResponse,
  UpdateBatchVehicleApiArg,
  UpdateBatchVehicleApiResponse,
} from '../types/api/api';

export const importApi = createApi({
  reducerPath: 'import',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.IMPORT_PRICE_REPORT_API_URL,
  }),
  tagTypes: ['Batch', 'BatchVehicles'],
  endpoints: (build) => ({
    listBatches: build.query<ListBatchesApiResponse, ListBatchesApiArg>({
      query: () => ({url: '/api/batches'}),
      providesTags: (result) => result?.data?.map((b) => ({type: 'Batch', id: b.id})) ?? [],
    }),
    createBatch: build.mutation<CreateBatchApiResponse, CreateBatchApiArg>({
      query: (queryArg) => {
        const formData = new FormData();
        formData.append('file', queryArg.body.file);
        formData.append('market', queryArg.body.market);
        if (isNotNil(queryArg.body.user_id)) {
          formData.append('user_id', queryArg.body.user_id);
        }
        if (isNotNil(queryArg.body.use_vin_decoding)) {
          formData.append('use_vin_decoding', String(queryArg.body.use_vin_decoding));
        }
        if (isNotNil(queryArg.body.name)) {
          formData.append('name', queryArg.body.name);
        }
        if (isNotNil(queryArg.body.lang)) {
          formData.append('lang', queryArg.body.lang);
        }
        return {
          url: '/api/batches',
          method: 'POST',
          body: formData,
        };
      },
    }),
    getBatch: build.query<GetBatchApiResponse, GetBatchApiArg>({
      query: (queryArg) => ({url: `/api/batches/${queryArg.batchId}`}),
      providesTags: (_result, _error, queryArg) => [{type: 'Batch', id: queryArg.batchId}],
    }),
    listBatchVehicles: build.query<ListBatchVehiclesApiResponse, ListBatchVehiclesApiArg>({
      query: (queryArg) => ({
        url: `/api/batches/${queryArg.batchId}/vehicles`,
        params: {page: queryArg.page, limit: queryArg.limit},
      }),
      providesTags: (_result, _error, queryArg) => [{type: 'BatchVehicles', id: queryArg.batchId}],
    }),
    updateBatchVehicle: build.mutation<UpdateBatchVehicleApiResponse, UpdateBatchVehicleApiArg>({
      query: (queryArg) => ({
        url: `/api/batches/${queryArg.batchId}/vehicles/${queryArg.vehicleId}`,
        method: 'PUT',
        body: queryArg.updateVehicleRequest,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'BatchVehicles', id: queryArg.batchId},
        {type: 'Batch', id: queryArg.batchId},
      ],
    }),
    resolveVehicleWarnings: build.mutation<
      ResolveVehicleWarningsApiResponse,
      ResolveVehicleWarningsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/batches/${queryArg.batchId}/vehicles/${queryArg.vehicleId}/resolve-warnings`,
        method: 'PATCH',
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'BatchVehicles', id: queryArg.batchId},
        {type: 'Batch', id: queryArg.batchId},
      ],
    }),
    setVehicleUnresolvedWarnings: build.mutation<
      SetVehicleUnresolvedWarningsApiResponse,
      SetVehicleUnresolvedWarningsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/batches/${queryArg.batchId}/vehicles/${queryArg.vehicleId}/unresolved-warnings`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'BatchVehicles', id: queryArg.batchId},
        {type: 'Batch', id: queryArg.batchId},
      ],
    }),
    requestPriceReport: build.mutation<RequestPriceReportApiResponse, RequestPriceReportApiArg>({
      query: (queryArg) => ({
        url: `/api/batches/${queryArg.batchId}/price-reports`,
        method: 'POST',
      }),
      // TODO: Invalidate also vehicle list of a batch if reports are returned there
      invalidatesTags: (_result, _error, queryArg) => [{type: 'Batch', id: queryArg.batchId}],
    }),
  }),
});

export const {
  useListBatchesQuery,
  useCreateBatchMutation,
  useGetBatchQuery,
  useListBatchVehiclesQuery,
  useUpdateBatchVehicleMutation,
  useResolveVehicleWarningsMutation,
  useSetVehicleUnresolvedWarningsMutation,
  useRequestPriceReportMutation,
} = importApi;
